import React, { useState, useEffect } from 'react';
import { ConfigProvider } from 'antd';
import { DeleteSolicitacao, GetAllSolicitacoes } from "@services/Solicitacao/Index.jsx";
import { DeleteOptionComp } from '@components/UI/DeleteOption/Index.jsx';
import StatusSolicitacaoComp from '@components/UI/StatusSolicitacao/Index.jsx';
import { formatDateToBR } from '@utils/FormatDate.jsx';
import { NavigateSolicitacao } from '@components/Solicitacao/Shared/NavigateSolicitacao/Index.jsx';
import ptBR from 'antd/es/locale/pt_BR';
import { StyledTable } from "@styles/datatables/datatable-antd.js";
import { Spinner } from '@styles/loading.js';
import TextBlock from "@components/UI/TextBlock/Index..jsx";
import { useDispatch } from 'react-redux';
import { limparFormularioAssociadoProcesso } from '@store/slices/formularios/Processo/formularioAssociadoProcessoSlice';

const GridConsultasSolicitacoes = ({ state, setData, onDelete, filters }) => {
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const dispatch = useDispatch();

  // Busca os dados da API
  const fetchData = async (currentPage, currentRowsPerPage, filters = {}) => {
    setLoading(true);
    try {
      const response = await GetAllSolicitacoes(currentPage, currentRowsPerPage, filters);
      setData(response.items, response.totalItems);
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  // Atualiza os dados quando a página ou o número de linhas por página muda
  useEffect(() => {
    fetchData(page, rowsPerPage, {
      tipoCompliance: filters.tipoCompliance || "",
      numeroSolicitacao: filters.numeroSolicitacao || "",
      dataSolicitacaoInicial: filters.dataSolicitacaoInicial || "",
      dataSolicitacaoFinal: filters.dataSolicitacaoFinal || "",
      nomeAuditor: filters.nomeAuditor || "",
      nomeProcesso: filters.nomeProcesso || "",
      nomeUnidadeGerencia: filters.nomeUnidadeGerencia || "",
      statusSolicitacao: filters.statusSolicitacao || "",
    });

  }, [page, rowsPerPage, filters]);

  // Manipula a mudança de página
  const handlePageChange = (newPage, newRowsPerPage) => {
    setPage(newPage);
    if (newRowsPerPage) {
      setRowsPerPage(newRowsPerPage);
    }
  };

  const getConfirmMessage = (row) => {
    return (
      `Esta ação será irreversível e excluirá o seguinte cadastro de solicitação: 
      <strong>${row.numeroSolicitacao}</strong>, 
      além dos documentos associados aos itens do processo.`
    )
  };

  // Colunas da tabela
  const columns = [
    {
      title: 'NÚMERO',
      dataIndex: 'numeroSolicitacao',
      key: 'numeroSolicitacao',
      width: 150,
      sorter: (a, b) => a.numeroSolicitacao.localeCompare(b.numeroSolicitacao),
    },
    {
      title: 'DATA CRIAÇÃO',
      key: 'dataSolicitacao',
      width: 150,
      render: (_, record) => formatDateToBR(record.dataSolicitacao),
      sorter: (a, b) => new Date(a.dataSolicitacao) - new Date(b.dataSolicitacao),
    },
    {
      title: 'AUDITOR',
      dataIndex: ['auditor', 'nome'],
      key: 'auditor',
      width: 180,
      sorter: (a, b) => a.auditor.nome.localeCompare(b.auditor.nome),
      render: (text) => (
        <TextBlock>
          {text}
        </TextBlock>
      ),
    },
    {
      title: 'PROCESSO',
      dataIndex: ['compliance', 'nomeProcesso'],
      key: 'nomeProcesso',
      width: 180,
      sorter: (a, b) => a.compliance.nomeProcesso.localeCompare(b.compliance.nomeProcesso),
      render: (text) => (
        <TextBlock>
          {text}
        </TextBlock>
      ),
    },
    {
      title: 'UNIDADE/GERÊNCIA',
      dataIndex: ['unidadeGerencia', 'nome'],
      key: 'nome',
      width: 200,
      sorter: (a, b) => a.unidadeGerencia.nome.localeCompare(b.unidade.nome),
      render: (text) => (
        <TextBlock>
          {text}
        </TextBlock>
      ),
    },
    {
      title: 'STATUS',
      key: 'statusSolicitacao',
      width: 150,
      render: (_, record) => <StatusSolicitacaoComp status={record.statusSolicitacao} />,
      sorter: (a, b) => a.statusSolicitacao - b.statusSolicitacao,
    },
    {
      title: 'DATA ENVIO',
      key: 'dataAgendarEnvio',
      width: 150,
      render: (_, record) => formatDateToBR(record.dataAgendarEnvio) || 'N/A',
    },
    {
      title: 'AÇÕES',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <div className="d-flex justify-content-center gap-4">
          <NavigateSolicitacao row={record} />
          <DeleteOptionComp
            row={record}
            deleteFunction={DeleteSolicitacao}
            onDelete={onDelete}
            itemNameKey={"numeroSolicitacao"}
            deleteReduxAction={dispatch(limparFormularioAssociadoProcesso(record.compliance?.idProcesso))}
            getConfirmMessage={getConfirmMessage}
          />
        </div>
      ),
    },
  ];

  return (
    <div className='px-4'>

      <div className='mt-3 mb-3'>
        <i className="fa-solid fa-file fs-6" style={{ color: '#2b97be' }} />
        <span className='p-2 datatable-title'>Consulta - Solicitações</span>
      </div>

      <ConfigProvider locale={ptBR}> {/* Localização em português */}
        <StyledTable
          columns={columns}
          dataSource={state.data}
          rowKey="numeroSolicitacao" // Chave única para cada linha
          loading={{
            spinning: loading,
            indicator: <Spinner />, // Spinner personalizado
          }}
          pagination={{
            current: page,
            pageSize: rowsPerPage,
            total: state.totalRows, // Total de registros
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: handlePageChange,
            onShowSizeChange: handlePageChange,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`, // Exibe o total de registros
          }}
          scroll={{ y: '20rem', x: 'max-content' }}
          locale={{
            emptyText: <h2 className='m-3'>Nenhuma solicitação disponível para exibição.</h2>, // Mensagem quando não há dados
          }}
        />
      </ConfigProvider>
    </div>
  );
};

export default GridConsultasSolicitacoes;